import { SpeciesEntry } from './bioacoustic.types';

export interface BioDeployment {
  deployment_id: string;
  deployment_name: string;
  device_id: string;
  device_type: string;
  site_id: string;
  location_name: string;
  latitude: number;
  longitude: number;
  totalSpecies: number;
  totalAudioFiles: number;
  totalDetections: number;
  avgConfidence: number;
  startDate: string;
  endDate: string;
  status: 'ACTIVE' | 'COMPLETED' | 'MAINTENANCE';
  threatDistribution: { name: string; count: number; color: string }[];
  speciesCatalog: (SpeciesEntry & {
    threatLevel: 'Endangered' | 'Vulnerable' | 'Least Concerned' | 'Unknown';
    audioSample?: {
      fileName: string;
      duration: string;
      timestamp: string;
      scientificName: string;
      detectedSpeciesList: { name: string; scientificName: string; confidence: number; imageUrl?: string }[];
    };
  })[];
}

export interface BioSite {
  site_id: string;
  site_name: string;
  project_id: string;
  latitude: number;
  longitude: number;
  centroid_wkt: string;
  boundaryCoordinates?: [number, number][];
  totalSpecies: number;
  totalAudioFiles: number;
  totalDeployments: number;
  deployments: BioDeployment[];
  threatDistribution: { name: string; count: number; color: string }[];
}

export interface BioProject {
  project_id: string;
  project_name: string;
  totalSpecies: number;
  totalAudioFiles: number;
  totalSites: number;
  totalDeployments: number;
  sites: BioSite[];
  threatDistribution: { name: string; count: number; color: string }[];
}

// ── 24h Time Slots ──────────────────────────────────────────────────────────
export const TIME_SLOTS_24H = [
  '00:00', '01:30', '03:00', '04:30', '06:00', '07:30', '09:00', '10:30',
  '12:00', '13:30', '15:00', '16:30', '18:00', '19:30', '21:00', '22:30'
];

// ── Diurnal Species Activity Curve Mock Data (Expanded for 10-Item Pagination) ───
export const MOCK_SPECIES_ACTIVITY_TREND = [
  {
    name: 'Amazona farinosa (Mealy Parrot)',
    color: '#00D4FF', // Cyan blue
    data: [0, 0, 0, 15, 250, 150, 30, 20, 0, 0, 135, 120, 10, 0, 0, 0],
  },
  {
    name: 'Ara macao (Scarlet Macaw)',
    color: '#3B82F6', // Blue
    data: [0, 0, 0, 10, 235, 20, 10, 85, 0, 0, 15, 125, 40, 20, 0, 0],
  },
  {
    name: 'Ramphastos tucanus (Toucan)',
    color: '#D946EF', // Magenta
    data: [0, 0, 0, 0, 25, 160, 65, 95, 0, 0, 20, 25, 15, 35, 0, 0],
  },
  {
    name: 'Cebus albifrons (White-fronted Capuchin)',
    color: '#10B981', // Emerald Green
    data: [0, 0, 0, 0, 45, 115, 15, 10, 0, 0, 75, 10, 5, 0, 0, 0],
  },
  {
    name: 'Gryllidae spp. (Tropical Cricket)',
    color: '#EF4444', // Red
    data: [0, 0, 0, 35, 10, 0, 5, 0, 0, 0, 45, 10, 70, 75, 0, 0],
  },
  {
    name: 'Morpho menelaus (Blue Morpho)',
    color: '#06B6D4', // Teal
    data: [0, 0, 0, 5, 15, 40, 95, 45, 0, 0, 85, 30, 20, 80, 0, 0],
  },
  {
    name: 'Pavo cristatus (Indian Peafowl)',
    color: '#F59E0B', // Amber
    data: [0, 0, 0, 30, 180, 140, 50, 10, 0, 0, 60, 95, 20, 0, 0, 0],
  },
  {
    name: 'Buceros bicornis (Great Hornbill)',
    color: '#EC4899', // Pink
    data: [0, 0, 0, 5, 110, 195, 80, 40, 0, 0, 40, 80, 35, 10, 0, 0],
  },
  {
    name: 'Haliaeetus leucogaster (Sea Eagle)',
    color: '#8B5CF6', // Purple
    data: [0, 0, 0, 0, 40, 90, 120, 70, 0, 0, 30, 45, 10, 0, 0, 0],
  },
  {
    name: 'Glaucidium radiatum (Jungle Owlet)',
    color: '#A855F7', // Violet
    data: [45, 80, 110, 95, 20, 0, 0, 0, 0, 0, 0, 0, 15, 60, 90, 70],
  },
  {
    name: 'Psittacula krameri (Rose-ringed Parakeet)',
    color: '#14B8A6', // Cyan green
    data: [0, 0, 0, 20, 210, 160, 45, 15, 0, 0, 90, 110, 25, 0, 0, 0],
  },
  {
    name: 'Anthracoceros albirostris (Pied Hornbill)',
    color: '#EAB308', // Yellow
    data: [0, 0, 0, 0, 75, 130, 60, 25, 0, 0, 35, 65, 15, 0, 0, 0],
  },
  {
    name: 'Centropus sinensis (Greater Coucal)',
    color: '#F97316', // Orange
    data: [0, 0, 0, 10, 95, 85, 30, 0, 0, 0, 20, 40, 15, 0, 0, 0],
  },
  {
    name: 'Pipistrellus coromandra (Coromandel Bat)',
    color: '#6366F1', // Indigo
    data: [85, 120, 95, 40, 0, 0, 0, 0, 0, 0, 0, 0, 10, 70, 140, 110],
  },
  {
    name: 'Megaderma lyra (Greater False Vampire Bat)',
    color: '#4F46E5', // Deep Indigo
    data: [60, 90, 110, 30, 0, 0, 0, 0, 0, 0, 0, 0, 5, 45, 95, 80],
  },
  {
    name: 'Apis dorsata (Giant Honey Bee)',
    color: '#FBBF24', // Amber
    data: [0, 0, 0, 0, 15, 60, 130, 160, 90, 0, 110, 70, 30, 0, 0, 0],
  },
];

// ── Acoustic Indices Normalized 0-1 (Matching Image 3) ──────────────────────
export const MOCK_ACOUSTIC_INDICES = {
  aci: [0.03, 0.03, 0.03, 0.08, 0.02, 0.22, 0.18, 0.04, 0.38, 0.03, 0.03, 0.22, 0.12, 0.98, 0.10, 0.15],
  bi:  [0.02, 0.02, 0.02, 0.92, 1.00, 0.38, 0.32, 0.25, 0.35, 0.02, 0.02, 0.08, 0.24, 0.32, 0.52, 0.50],
  adi: [0.03, 0.03, 0.03, 0.18, 0.32, 0.88, 0.85, 0.52, 0.62, 0.03, 0.03, 0.20, 0.65, 0.92, 0.34, 0.52],
  aei: [0.02, 0.02, 0.02, 0.68, 0.85, 0.02, 0.14, 0.92, 0.58, 0.02, 0.02, 0.85, 0.28, 0.42, 0.98, 0.84],
  ndsi:[0.03, 0.03, 0.03, 0.48, 0.05, 0.05, 0.72, 0.88, 0.48, 0.03, 0.03, 1.00, 0.78, 0.58, 0.84, 0.72],
};

// ── Deployment-wise Comparison Across Entire Project ────────────────────────
export const MOCK_DEPLOYMENT_COMPARISON: Record<string, number[]> = {
  'dep-1': [0, 0, 0, 520, 525, 510, 545, 530, 570, 0, 0, 535, 580, 530, 540, 510], // Baseline (Karpa C2 S2)
  'dep-2': [0, 0, 0, 0, 510, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0], // Sample Station (Karpa C2 S3)
  'dep-3': [0, 0, 0, 310, 480, 420, 390, 460, 410, 0, 0, 450, 490, 430, 410, 380], // Canopy Tower Station
  'dep-4': [0, 0, 0, 180, 220, 310, 290, 240, 260, 0, 0, 280, 310, 290, 270, 230], // Masira Corridor Node
  'dep-5': [0, 0, 0, 240, 380, 395, 360, 310, 340, 0, 0, 390, 410, 370, 330, 280], // Masira Ridge Understory
  'dep-6': [0, 0, 0, 410, 460, 430, 480, 420, 490, 0, 0, 470, 510, 460, 440, 390], // Sonanadi Riverbed Node
  'dep-7': [0, 0, 0, 290, 340, 310, 360, 330, 350, 0, 0, 380, 400, 350, 330, 290], // Sonanadi Buffer Outpost
};

// ── Species Catalogue with Audio Log records for Analyzer ───────────────────
export const MOCK_SPECIES_CATALOG_EXTENDED: (SpeciesEntry & {
  threatLevel: 'Endangered' | 'Vulnerable' | 'Least Concerned' | 'Unknown';
  audioSample: {
    fileName: string;
    duration: string;
    timestamp: string;
    scientificName: string;
    detectedSpeciesList: { name: string; scientificName: string; confidence: number }[];
  };
})[] = [
  {
    name: 'Ara macao',
    commonName: 'Scarlet Macaw',
    family: 'Psittacidae',
    detections: 104,
    confidence: 94,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_061500_ScarletMacaw.wav',
      duration: '15:00',
      timestamp: '2026-01-05 06:15:00 UTC',
      scientificName: 'Ara macao',
      detectedSpeciesList: [
        { name: 'Scarlet Macaw', scientificName: 'Ara macao', confidence: 94 },
        { name: 'Mealy Parrot', scientificName: 'Amazona farinosa', confidence: 88 },
      ],
    },
  },
  {
    name: 'Amazona farinosa',
    commonName: 'Mealy Parrot',
    family: 'Psittacidae',
    detections: 134,
    confidence: 96,
    type: 'AVIAN',
    threatLevel: 'Vulnerable',
    audioSample: {
      fileName: 'AM_20260105_063000_MealyParrot.wav',
      duration: '15:00',
      timestamp: '2026-01-05 06:30:00 UTC',
      scientificName: 'Amazona farinosa',
      detectedSpeciesList: [
        { name: 'Mealy Parrot', scientificName: 'Amazona farinosa', confidence: 96 },
      ],
    },
  },
  {
    name: 'Ramphastos tucanus',
    commonName: 'White-throated Toucan',
    family: 'Ramphastidae',
    detections: 56,
    confidence: 88,
    type: 'AVIAN',
    threatLevel: 'Vulnerable',
    audioSample: {
      fileName: 'AM_20260105_071500_ToucanCall.wav',
      duration: '15:00',
      timestamp: '2026-01-05 07:15:00 UTC',
      scientificName: 'Ramphastos tucanus',
      detectedSpeciesList: [
        { name: 'White-throated Toucan', scientificName: 'Ramphastos tucanus', confidence: 90 },
      ],
    },
  },
  {
    name: 'Pavo cristatus',
    commonName: 'Indian Peafowl',
    family: 'Phasianidae',
    detections: 92,
    confidence: 93,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_054500_PeafowlMayAwk.wav',
      duration: '15:00',
      timestamp: '2026-01-05 05:45:00 UTC',
      scientificName: 'Pavo cristatus',
      detectedSpeciesList: [
        { name: 'Indian Peafowl', scientificName: 'Pavo cristatus', confidence: 93 },
      ],
    },
  },
  {
    name: 'Buceros bicornis',
    commonName: 'Great Hornbill',
    family: 'Bucerotidae',
    detections: 68,
    confidence: 90,
    type: 'AVIAN',
    threatLevel: 'Vulnerable',
    audioSample: {
      fileName: 'AM_20260105_080000_GreatHornbillRoar.wav',
      duration: '15:00',
      timestamp: '2026-01-05 08:00:00 UTC',
      scientificName: 'Buceros bicornis',
      detectedSpeciesList: [
        { name: 'Great Hornbill', scientificName: 'Buceros bicornis', confidence: 90 },
      ],
    },
  },
  {
    name: 'Gyps bengalensis',
    commonName: 'White-rumped Vulture',
    family: 'Accipitridae',
    detections: 18,
    confidence: 95,
    type: 'AVIAN',
    threatLevel: 'Endangered',
    audioSample: {
      fileName: 'AM_20260105_110000_VultureNestCall.wav',
      duration: '15:00',
      timestamp: '2026-01-05 11:00:00 UTC',
      scientificName: 'Gyps bengalensis',
      detectedSpeciesList: [
        { name: 'White-rumped Vulture', scientificName: 'Gyps bengalensis', confidence: 95 },
      ],
    },
  },
  {
    name: 'Sarcogyps calvus',
    commonName: 'Red-headed Vulture',
    family: 'Accipitridae',
    detections: 12,
    confidence: 92,
    type: 'AVIAN',
    threatLevel: 'Endangered',
    audioSample: {
      fileName: 'AM_20260105_121500_RedHeadedVulture.wav',
      duration: '15:00',
      timestamp: '2026-01-05 12:15:00 UTC',
      scientificName: 'Sarcogyps calvus',
      detectedSpeciesList: [
        { name: 'Red-headed Vulture', scientificName: 'Sarcogyps calvus', confidence: 92 },
      ],
    },
  },
  {
    name: 'Grus antigone',
    commonName: 'Sarus Crane',
    family: 'Gruidae',
    detections: 34,
    confidence: 91,
    type: 'AVIAN',
    threatLevel: 'Vulnerable',
    audioSample: {
      fileName: 'AM_20260105_060000_SarusCraneDuet.wav',
      duration: '15:00',
      timestamp: '2026-01-05 06:00:00 UTC',
      scientificName: 'Grus antigone',
      detectedSpeciesList: [
        { name: 'Sarus Crane', scientificName: 'Grus antigone', confidence: 91 },
      ],
    },
  },
  {
    name: 'Haliaeetus leucogaster',
    commonName: 'White-bellied Sea Eagle',
    family: 'Accipitridae',
    detections: 45,
    confidence: 89,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_091500_SeaEagleWhistle.wav',
      duration: '15:00',
      timestamp: '2026-01-05 09:15:00 UTC',
      scientificName: 'Haliaeetus leucogaster',
      detectedSpeciesList: [
        { name: 'White-bellied Sea Eagle', scientificName: 'Haliaeetus leucogaster', confidence: 89 },
      ],
    },
  },
  {
    name: 'Glaucidium radiatum',
    commonName: 'Jungle Owlet',
    family: 'Strigidae',
    detections: 62,
    confidence: 87,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_214500_JungleOwletTrill.wav',
      duration: '15:00',
      timestamp: '2026-01-05 21:45:00 UTC',
      scientificName: 'Glaucidium radiatum',
      detectedSpeciesList: [
        { name: 'Jungle Owlet', scientificName: 'Glaucidium radiatum', confidence: 87 },
      ],
    },
  },
  {
    name: 'Psittacula krameri',
    commonName: 'Rose-ringed Parakeet',
    family: 'Psittacidae',
    detections: 118,
    confidence: 96,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_073000_ParakeetFlock.wav',
      duration: '15:00',
      timestamp: '2026-01-05 07:30:00 UTC',
      scientificName: 'Psittacula krameri',
      detectedSpeciesList: [
        { name: 'Rose-ringed Parakeet', scientificName: 'Psittacula krameri', confidence: 96 },
      ],
    },
  },
  {
    name: 'Anthracoceros albirostris',
    commonName: 'Oriental Pied Hornbill',
    family: 'Bucerotidae',
    detections: 54,
    confidence: 89,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_083000_PiedHornbillCackle.wav',
      duration: '15:00',
      timestamp: '2026-01-05 08:30:00 UTC',
      scientificName: 'Anthracoceros albirostris',
      detectedSpeciesList: [
        { name: 'Oriental Pied Hornbill', scientificName: 'Anthracoceros albirostris', confidence: 89 },
      ],
    },
  },
  {
    name: 'Centropus sinensis',
    commonName: 'Greater Coucal',
    family: 'Cuculidae',
    detections: 48,
    confidence: 86,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_064500_CoucalBoop.wav',
      duration: '15:00',
      timestamp: '2026-01-05 06:45:00 UTC',
      scientificName: 'Centropus sinensis',
      detectedSpeciesList: [
        { name: 'Greater Coucal', scientificName: 'Centropus sinensis', confidence: 86 },
      ],
    },
  },
  {
    name: 'Dinopium benghalense',
    commonName: 'Black-rumped Flameback',
    family: 'Picidae',
    detections: 39,
    confidence: 88,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_100000_FlamebackDrum.wav',
      duration: '15:00',
      timestamp: '2026-01-05 10:00:00 UTC',
      scientificName: 'Dinopium benghalense',
      detectedSpeciesList: [
        { name: 'Black-rumped Flameback', scientificName: 'Dinopium benghalense', confidence: 88 },
      ],
    },
  },
  {
    name: 'Halcyon smyrnensis',
    commonName: 'White-throated Kingfisher',
    family: 'Alcedinidae',
    detections: 58,
    confidence: 92,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_081500_KingfisherRattle.wav',
      duration: '15:00',
      timestamp: '2026-01-05 08:15:00 UTC',
      scientificName: 'Halcyon smyrnensis',
      detectedSpeciesList: [
        { name: 'White-throated Kingfisher', scientificName: 'Halcyon smyrnensis', confidence: 92 },
      ],
    },
  },
  {
    name: 'Copsychus saularis',
    commonName: 'Oriental Magpie-Robin',
    family: 'Muscicapidae',
    detections: 73,
    confidence: 94,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_053000_MagpieRobinDawn.wav',
      duration: '15:00',
      timestamp: '2026-01-05 05:30:00 UTC',
      scientificName: 'Copsychus saularis',
      detectedSpeciesList: [
        { name: 'Oriental Magpie-Robin', scientificName: 'Copsychus saularis', confidence: 94 },
      ],
    },
  },
  {
    name: 'Dicrurus macrocercus',
    commonName: 'Black Drongo',
    family: 'Dicruridae',
    detections: 67,
    confidence: 91,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_070000_BlackDrongoMimic.wav',
      duration: '15:00',
      timestamp: '2026-01-05 07:00:00 UTC',
      scientificName: 'Dicrurus macrocercus',
      detectedSpeciesList: [
        { name: 'Black Drongo', scientificName: 'Dicrurus macrocercus', confidence: 91 },
      ],
    },
  },
  {
    name: 'Chalcophaps indica',
    commonName: 'Common Emerald Dove',
    family: 'Columbidae',
    detections: 41,
    confidence: 85,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_094500_EmeraldDoveCoo.wav',
      duration: '15:00',
      timestamp: '2026-01-05 09:45:00 UTC',
      scientificName: 'Chalcophaps indica',
      detectedSpeciesList: [
        { name: 'Common Emerald Dove', scientificName: 'Chalcophaps indica', confidence: 85 },
      ],
    },
  },
  {
    name: 'Argya striata',
    commonName: 'Jungle Babbler',
    family: 'Leiothrichidae',
    detections: 83,
    confidence: 93,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_084500_BabblerChatter.wav',
      duration: '15:00',
      timestamp: '2026-01-05 08:45:00 UTC',
      scientificName: 'Argya striata',
      detectedSpeciesList: [
        { name: 'Jungle Babbler', scientificName: 'Argya striata', confidence: 93 },
      ],
    },
  },
  {
    name: 'Athene brama',
    commonName: 'Spotted Owlet',
    family: 'Strigidae',
    detections: 51,
    confidence: 89,
    type: 'AVIAN',
    threatLevel: 'Least Concerned',
    audioSample: {
      fileName: 'AM_20260105_223000_SpottedOwletChuckle.wav',
      duration: '15:00',
      timestamp: '2026-01-05 22:30:00 UTC',
      scientificName: 'Athene brama',
      detectedSpeciesList: [
        { name: 'Spotted Owlet', scientificName: 'Athene brama', confidence: 89 },
      ],
    },
  },
  {
    name: 'Picus squamatus',
    commonName: 'Scaly-bellied Woodpecker',
    family: 'Picidae',
    detections: 22,
    confidence: 84,
    type: 'AVIAN',
    threatLevel: 'Unknown',
    audioSample: {
      fileName: 'AM_20260105_104500_ScalyWoodpecker.wav',
      duration: '15:00',
      timestamp: '2026-01-05 10:45:00 UTC',
      scientificName: 'Picus squamatus',
      detectedSpeciesList: [
        { name: 'Scaly-bellied Woodpecker', scientificName: 'Picus squamatus', confidence: 84 },
      ],
    },
  },
  {
    name: 'Spizaetus cirrhatus',
    commonName: 'Changeable Hawk-Eagle',
    family: 'Accipitridae',
    detections: 27,
    confidence: 87,
    type: 'AVIAN',
    threatLevel: 'Unknown',
    audioSample: {
      fileName: 'AM_20260105_130000_HawkEagleScream.wav',
      duration: '15:00',
      timestamp: '2026-01-05 13:00:00 UTC',
      scientificName: 'Spizaetus cirrhatus',
      detectedSpeciesList: [
        { name: 'Changeable Hawk-Eagle', scientificName: 'Spizaetus cirrhatus', confidence: 87 },
      ],
    },
  },
  {
    name: 'Ketupa zeylonensis',
    commonName: 'Brown Fish Owl',
    family: 'Strigidae',
    detections: 19,
    confidence: 86,
    type: 'AVIAN',
    threatLevel: 'Unknown',
    audioSample: {
      fileName: 'AM_20260105_231500_FishOwlBoom.wav',
      duration: '15:00',
      timestamp: '2026-01-05 23:15:00 UTC',
      scientificName: 'Ketupa zeylonensis',
      detectedSpeciesList: [
        { name: 'Brown Fish Owl', scientificName: 'Ketupa zeylonensis', confidence: 86 },
      ],
    },
  },
];

// ── Complete Hierarchy Mock Tree ────────────────────────────────────────────
export const MOCK_PROJECT_HIERARCHY: BioProject = {
  project_id: 'proj-corbett-bio',
  project_name: 'Corbett Landscape Conservation',
  totalSpecies: 23,
  totalAudioFiles: 4820,
  totalSites: 3,
  totalDeployments: 7,
  threatDistribution: [
    { name: 'Least Concerned', count: 14, color: '#10B981' },
    { name: 'Unknown', count: 3, color: '#F97316' },
    { name: 'Vulnerable', count: 4, color: '#8B5CF6' },
    { name: 'Endangered', count: 2, color: '#00D4FF' },
  ],
  sites: [
    {
      site_id: 'site-corbett-north',
      site_name: 'Corbett Buffer Zone - Karpa',
      project_id: 'proj-corbett-bio',
      latitude: 29.5300,
      longitude: 78.7747,
      centroid_wkt: 'POINT (78.7747 29.5300)',
      boundaryCoordinates: [
        [78.760, 29.540],
        [78.788, 29.544],
        [78.796, 29.522],
        [78.765, 29.516],
        [78.760, 29.540],
      ],
      totalSpecies: 17,
      totalAudioFiles: 1840,
      totalDeployments: 3,
      threatDistribution: [
        { name: 'Least Concerned', count: 10, color: '#10B981' },
        { name: 'Unknown', count: 2, color: '#F97316' },
        { name: 'Vulnerable', count: 3, color: '#8B5CF6' },
        { name: 'Endangered', count: 2, color: '#00D4FF' },
      ],
      deployments: [
        {
          deployment_id: 'dep-1',
          deployment_name: 'Baseline Station (Karpa C2 S2)',
          device_id: 'AUDIOMOTH-94A2',
          device_type: 'AudioMoth v1.2',
          site_id: 'site-corbett-north',
          location_name: 'Karpa Ridge Topography',
          latitude: 29.5320,
          longitude: 78.7760,
          totalSpecies: 13,
          totalAudioFiles: 620,
          totalDetections: 153,
          avgConfidence: 87,
          startDate: '2025-10-05',
          endDate: '2026-01-05',
          status: 'ACTIVE',
          threatDistribution: [
            { name: 'Least Concerned', count: 8, color: '#10B981' },
            { name: 'Unknown', count: 2, color: '#F97316' },
            { name: 'Vulnerable', count: 2, color: '#8B5CF6' },
            { name: 'Endangered', count: 1, color: '#00D4FF' },
          ],
          speciesCatalog: MOCK_SPECIES_CATALOG_EXTENDED,
        },
        {
          deployment_id: 'dep-2',
          deployment_name: 'Sample Station (Karpa C2 S3)',
          device_id: 'AUDIOMOTH-83B1',
          device_type: 'AudioMoth v1.2',
          site_id: 'site-corbett-north',
          location_name: 'Karpa Riverbed Stream',
          latitude: 29.5285,
          longitude: 78.7730,
          totalSpecies: 11,
          totalAudioFiles: 580,
          totalDetections: 198,
          avgConfidence: 89,
          startDate: '2025-11-01',
          endDate: '2026-01-20',
          status: 'ACTIVE',
          threatDistribution: [
            { name: 'Least Concerned', count: 7, color: '#10B981' },
            { name: 'Unknown', count: 1, color: '#F97316' },
            { name: 'Vulnerable', count: 2, color: '#8B5CF6' },
            { name: 'Endangered', count: 1, color: '#00D4FF' },
          ],
          speciesCatalog: MOCK_SPECIES_CATALOG_EXTENDED,
        },
        {
          deployment_id: 'dep-3',
          deployment_name: 'Canopy Tower Station',
          device_id: 'AUDIOMOTH-55C9',
          device_type: 'AudioMoth v1.2',
          site_id: 'site-corbett-north',
          location_name: 'Karpa Canopy Tower',
          latitude: 29.5310,
          longitude: 78.7790,
          totalSpecies: 9,
          totalAudioFiles: 640,
          totalDetections: 130,
          avgConfidence: 85,
          startDate: '2025-10-15',
          endDate: '2026-01-15',
          status: 'ACTIVE',
          threatDistribution: [
            { name: 'Least Concerned', count: 6, color: '#10B981' },
            { name: 'Unknown', count: 1, color: '#F97316' },
            { name: 'Vulnerable', count: 1, color: '#8B5CF6' },
            { name: 'Endangered', count: 1, color: '#00D4FF' },
          ],
          speciesCatalog: MOCK_SPECIES_CATALOG_EXTENDED,
        },
      ],
    },
    {
      site_id: 'site-corbett-masira',
      site_name: 'Masira Dense Forest Corridor',
      project_id: 'proj-corbett-bio',
      latitude: 29.4850,
      longitude: 78.8250,
      centroid_wkt: 'POINT (78.8250 29.4850)',
      boundaryCoordinates: [
        [78.808, 29.498],
        [78.842, 29.496],
        [78.844, 29.474],
        [78.810, 29.470],
        [78.808, 29.498],
      ],
      totalSpecies: 15,
      totalAudioFiles: 1620,
      totalDeployments: 2,
      threatDistribution: [
        { name: 'Least Concerned', count: 9, color: '#10B981' },
        { name: 'Unknown', count: 2, color: '#F97316' },
        { name: 'Vulnerable', count: 3, color: '#8B5CF6' },
        { name: 'Endangered', count: 1, color: '#00D4FF' },
      ],
      deployments: [
        {
          deployment_id: 'dep-4',
          deployment_name: 'Masira Canopy Corridor (C4 S1)',
          device_id: 'AUDIOMOTH-42A7',
          device_type: 'AudioMoth v1.2',
          site_id: 'site-corbett-masira',
          location_name: 'Masira Canopy Outpost',
          latitude: 29.4860,
          longitude: 78.8260,
          totalSpecies: 10,
          totalAudioFiles: 820,
          totalDetections: 175,
          avgConfidence: 89,
          startDate: '2025-10-10',
          endDate: '2026-01-10',
          status: 'ACTIVE',
          threatDistribution: [
            { name: 'Least Concerned', count: 6, color: '#10B981' },
            { name: 'Unknown', count: 1, color: '#F97316' },
            { name: 'Vulnerable', count: 2, color: '#8B5CF6' },
            { name: 'Endangered', count: 1, color: '#00D4FF' },
          ],
          speciesCatalog: MOCK_SPECIES_CATALOG_EXTENDED,
        },
        {
          deployment_id: 'dep-5',
          deployment_name: 'Masira Ridge Understory (C4 S2)',
          device_id: 'AUDIOMOTH-43A8',
          device_type: 'AudioMoth v1.2',
          site_id: 'site-corbett-masira',
          location_name: 'Masira Ridge',
          latitude: 29.4840,
          longitude: 78.8240,
          totalSpecies: 9,
          totalAudioFiles: 800,
          totalDetections: 160,
          avgConfidence: 88,
          startDate: '2025-10-10',
          endDate: '2026-01-10',
          status: 'ACTIVE',
          threatDistribution: [
            { name: 'Least Concerned', count: 5, color: '#10B981' },
            { name: 'Unknown', count: 1, color: '#F97316' },
            { name: 'Vulnerable', count: 2, color: '#8B5CF6' },
            { name: 'Endangered', count: 1, color: '#00D4FF' },
          ],
          speciesCatalog: MOCK_SPECIES_CATALOG_EXTENDED,
        },
      ],
    },
    {
      site_id: 'site-corbett-pipri',
      site_name: 'Pipri Wetland & Grassland',
      project_id: 'proj-corbett-bio',
      latitude: 29.4200,
      longitude: 78.9100,
      centroid_wkt: 'POINT (78.9100 29.4200)',
      boundaryCoordinates: [
        [78.892, 29.432],
        [78.928, 29.430],
        [78.930, 29.408],
        [78.895, 29.405],
        [78.892, 29.432],
      ],
      totalSpecies: 13,
      totalAudioFiles: 1360,
      totalDeployments: 2,
      threatDistribution: [
        { name: 'Least Concerned', count: 8, color: '#10B981' },
        { name: 'Unknown', count: 2, color: '#F97316' },
        { name: 'Vulnerable', count: 2, color: '#8B5CF6' },
        { name: 'Endangered', count: 1, color: '#00D4FF' },
      ],
      deployments: [
        {
          deployment_id: 'dep-6',
          deployment_name: 'Pipri Wetland Marsh (C1 S1)',
          device_id: 'AUDIOMOTH-71D2',
          device_type: 'AudioMoth v1.2',
          site_id: 'site-corbett-pipri',
          location_name: 'Pipri Lake Marshland',
          latitude: 29.4210,
          longitude: 78.9120,
          totalSpecies: 11,
          totalAudioFiles: 710,
          totalDetections: 210,
          avgConfidence: 91,
          startDate: '2025-10-20',
          endDate: '2026-01-20',
          status: 'ACTIVE',
          threatDistribution: [
            { name: 'Least Concerned', count: 7, color: '#10B981' },
            { name: 'Unknown', count: 1, color: '#F97316' },
            { name: 'Vulnerable', count: 2, color: '#8B5CF6' },
            { name: 'Endangered', count: 1, color: '#00D4FF' },
          ],
          speciesCatalog: MOCK_SPECIES_CATALOG_EXTENDED,
        },
        {
          deployment_id: 'dep-7',
          deployment_name: 'Pipri Grassland Edge (C1 S2)',
          device_id: 'AUDIOMOTH-72D3',
          device_type: 'AudioMoth v1.2',
          site_id: 'site-corbett-pipri',
          location_name: 'Pipri Edge Buffer',
          latitude: 29.4190,
          longitude: 78.9080,
          totalSpecies: 8,
          totalAudioFiles: 650,
          totalDetections: 140,
          avgConfidence: 85,
          startDate: '2025-10-20',
          endDate: '2026-01-20',
          status: 'ACTIVE',
          threatDistribution: [
            { name: 'Least Concerned', count: 5, color: '#10B981' },
            { name: 'Unknown', count: 1, color: '#F97316' },
            { name: 'Vulnerable', count: 1, color: '#8B5CF6' },
            { name: 'Endangered', count: 1, color: '#00D4FF' },
          ],
          speciesCatalog: MOCK_SPECIES_CATALOG_EXTENDED,
        },
      ],
    },
  ],
};

// ── Donut Chart Species Breakdown for Interactive Hover Popover ─────────────
export interface DonutSpeciesItem {
  name: string;
  scientificName: string;
  family: string;
  detections: number;
  confidence: number;
  threatLevel: string;
  color: string;
}

export const MOCK_DONUT_SPECIES_BREAKDOWN: Record<string, DonutSpeciesItem[]> = {
  'Least Concerned': [
    { name: 'Scarlet Macaw', scientificName: 'Ara macao', family: 'Psittacidae', detections: 104, confidence: 91, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Blue Morpho', scientificName: 'Morpho menelaus', family: 'Nymphalidae', detections: 89, confidence: 83, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Tropical Cricket', scientificName: 'Gryllidae spp.', family: 'Gryllidae', detections: 77, confidence: 78, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Indian Peafowl', scientificName: 'Pavo cristatus', family: 'Phasianidae', detections: 68, confidence: 89, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Rose-ringed Parakeet', scientificName: 'Psittacula krameri', family: 'Psittacidae', detections: 64, confidence: 92, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Greater Coucal', scientificName: 'Centropus sinensis', family: 'Cuculidae', detections: 58, confidence: 84, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Jungle Owlet', scientificName: 'Glaucidium radiatum', family: 'Strigidae', detections: 52, confidence: 86, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'White-throated Kingfisher', scientificName: 'Halcyon smyrnensis', family: 'Alcedinidae', detections: 47, confidence: 88, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Oriental Magpie-Robin', scientificName: 'Copsychus saularis', family: 'Muscicapidae', detections: 44, confidence: 85, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Black Drongo', scientificName: 'Dicrurus macrocercus', family: 'Dicruridae', detections: 41, confidence: 87, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Spotted Dove', scientificName: 'Spilopelia chinensis', family: 'Columbidae', detections: 39, confidence: 90, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Red-vented Bulbul', scientificName: 'Pycnonotus cafer', family: 'Pycnonotidae', detections: 36, confidence: 89, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Asian Koel', scientificName: 'Eudynamys scolopaceus', family: 'Cuculidae', detections: 33, confidence: 82, threatLevel: 'Least Concerned', color: '#10B981' },
    { name: 'Rufous Treepie', scientificName: 'Dendrocitta vagabunda', family: 'Corvidae', detections: 30, confidence: 86, threatLevel: 'Least Concerned', color: '#10B981' },
  ],
  'Vulnerable': [
    { name: 'Mealy Parrot', scientificName: 'Amazona farinosa', family: 'Psittacidae', detections: 134, confidence: 94, threatLevel: 'Vulnerable', color: '#8B5CF6' },
    { name: 'White-throated Toucan', scientificName: 'Ramphastos tucanus', family: 'Ramphastidae', detections: 56, confidence: 88, threatLevel: 'Vulnerable', color: '#8B5CF6' },
    { name: 'Great Hornbill', scientificName: 'Buceros bicornis', family: 'Bucerotidae', detections: 48, confidence: 91, threatLevel: 'Vulnerable', color: '#8B5CF6' },
    { name: 'Pied Hornbill', scientificName: 'Anthracoceros albirostris', family: 'Bucerotidae', detections: 42, confidence: 87, threatLevel: 'Vulnerable', color: '#8B5CF6' },
  ],
  'Endangered': [
    { name: 'White-fronted Capuchin', scientificName: 'Cebus albifrons', family: 'Cebidae', detections: 89, confidence: 87, threatLevel: 'Endangered', color: '#00D4FF' },
    { name: 'White-bellied Sea Eagle', scientificName: 'Haliaeetus leucogaster', family: 'Accipitridae', detections: 24, confidence: 86, threatLevel: 'Endangered', color: '#00D4FF' },
  ],
  'Unknown': [
    { name: 'Unidentified Forest Stridulator', scientificName: 'Orthoptera sp. A', family: 'Orthoptera', detections: 38, confidence: 62, threatLevel: 'Unknown', color: '#F97316' },
    { name: 'High-frequency Canopy Bio-signal', scientificName: 'Chiroptera sp. B', family: 'Vespertilionidae', detections: 32, confidence: 58, threatLevel: 'Unknown', color: '#F97316' },
    { name: 'Riparian Lowland Acoustic Echo', scientificName: 'Aves sp. C', family: 'Incerta', detections: 28, confidence: 55, threatLevel: 'Unknown', color: '#F97316' },
  ],
};

// ── Average Diurnal Activity Profile Data for Species Modal ─────────────────
export interface SpeciesDiurnalProfile {
  speciesName: string;
  scientificName: string;
  family: string;
  category: string;
  threatLevel: string;
  totalDetections: number;
  avgConfidence: number;
  peakWindow: string;
  peakHour: string;
  timeSlots: string[];
  hourlyActivity: number[];
  frequencyBand: string;
  avgDecibels: number;
}

export const SPECIES_DIURNAL_PROFILES: Record<string, SpeciesDiurnalProfile> = {
  'Ara macao': {
    speciesName: 'Scarlet Macaw',
    scientificName: 'Ara macao',
    family: 'Psittacidae',
    category: 'Avian (Canopy)',
    threatLevel: 'Least Concerned',
    totalDetections: 104,
    avgConfidence: 91.4,
    peakWindow: '06:00 - 08:30 (Dawn Chorus)',
    peakHour: '06:00 AM',
    timeSlots: TIME_SLOTS_24H,
    hourlyActivity: [0, 0, 0, 10, 235, 20, 10, 85, 0, 0, 15, 125, 40, 20, 0, 0],
    frequencyBand: '1.2 kHz – 4.8 kHz',
    avgDecibels: -18.4,
  },
  'Amazona farinosa': {
    speciesName: 'Mealy Parrot',
    scientificName: 'Amazona farinosa',
    family: 'Psittacidae',
    category: 'Avian (Upper Canopy)',
    threatLevel: 'Vulnerable',
    totalDetections: 134,
    avgConfidence: 94.0,
    peakWindow: '06:00 - 08:00 (Dawn)',
    peakHour: '06:00 AM',
    timeSlots: TIME_SLOTS_24H,
    hourlyActivity: [0, 0, 0, 15, 250, 150, 30, 20, 0, 0, 135, 120, 10, 0, 0, 0],
    frequencyBand: '1.5 kHz – 5.2 kHz',
    avgDecibels: -16.2,
  },
  'Morpho menelaus': {
    speciesName: 'Blue Morpho',
    scientificName: 'Morpho menelaus',
    family: 'Nymphalidae',
    category: 'Pollinator',
    threatLevel: 'Least Concerned',
    totalDetections: 89,
    avgConfidence: 83.2,
    peakWindow: '09:00 - 14:00 (Solar Peak)',
    peakHour: '12:00 PM',
    timeSlots: TIME_SLOTS_24H,
    hourlyActivity: [0, 0, 0, 5, 15, 40, 95, 45, 0, 0, 85, 30, 20, 80, 0, 0],
    frequencyBand: '2.0 kHz – 8.0 kHz',
    avgDecibels: -26.5,
  },
  'Ramphastos tucanus': {
    speciesName: 'White-throated Toucan',
    scientificName: 'Ramphastos tucanus',
    family: 'Ramphastidae',
    category: 'Avian (Middle Canopy)',
    threatLevel: 'Vulnerable',
    totalDetections: 56,
    avgConfidence: 88.0,
    peakWindow: '07:30 - 10:30 (Mid-Morning)',
    peakHour: '07:30 AM',
    timeSlots: TIME_SLOTS_24H,
    hourlyActivity: [0, 0, 0, 0, 25, 160, 65, 95, 0, 0, 20, 25, 15, 35, 0, 0],
    frequencyBand: '0.8 kHz – 3.2 kHz',
    avgDecibels: -14.8,
  },
  'Cebus albifrons': {
    speciesName: 'White-fronted Capuchin',
    scientificName: 'Cebus albifrons',
    family: 'Cebidae',
    category: 'Bat / Primate Vocalizations',
    threatLevel: 'Endangered',
    totalDetections: 89,
    avgConfidence: 87.0,
    peakWindow: '07:30 - 09:30 & 15:00',
    peakHour: '07:30 AM',
    timeSlots: TIME_SLOTS_24H,
    hourlyActivity: [0, 0, 0, 0, 45, 115, 15, 10, 0, 0, 75, 10, 5, 0, 0, 0],
    frequencyBand: '0.6 kHz – 6.5 kHz',
    avgDecibels: -19.2,
  },
  'Gryllidae spp.': {
    speciesName: 'Tropical Cricket',
    scientificName: 'Gryllidae spp.',
    family: 'Gryllidae',
    category: 'Pollinator / Stridulator',
    threatLevel: 'Least Concerned',
    totalDetections: 77,
    avgConfidence: 78.5,
    peakWindow: '18:00 - 22:30 (Dusk & Night)',
    peakHour: '21:00 PM',
    timeSlots: TIME_SLOTS_24H,
    hourlyActivity: [0, 0, 0, 35, 10, 0, 5, 0, 0, 0, 45, 10, 70, 75, 0, 0],
    frequencyBand: '4.5 kHz – 9.8 kHz',
    avgDecibels: -22.1,
  },
};

// ── Highcharts Dark Theme Exporting & Context Menu Config ────────────────────
export const DARK_THEME_EXPORTING: any = {
  enabled: true,
  buttons: {
    contextButton: {
      symbol: 'menu',
      symbolStroke: '#888888',
      theme: {
        fill: '#151515',
        stroke: '#2A2A2A',
        r: 6,
        padding: 5,
        states: {
          hover: {
            fill: '#222222',
            stroke: '#10B981',
            symbolStroke: '#10B981',
          },
          select: {
            fill: '#10B981',
            stroke: '#10B981',
            symbolStroke: '#000000',
          },
        },
      },
    },
  },
};

export const DARK_THEME_NAVIGATION: any = {
  buttonOptions: {
    symbolStroke: '#888888',
    theme: {
      fill: '#151515',
      stroke: '#2A2A2A',
      r: 6,
      padding: 5,
      states: {
        hover: {
          fill: '#222222',
          stroke: '#10B981',
          symbolStroke: '#10B981',
        },
        select: {
          fill: '#10B981',
          stroke: '#10B981',
          symbolStroke: '#000000',
        },
      },
    },
  },
  menuStyle: {
    background: '#141414',
    border: '1px solid #282828',
    borderRadius: '8px',
    padding: '6px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8)',
  },
  menuItemStyle: {
    color: '#DDDDDD',
    fontSize: '11px',
    fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
    padding: '7px 14px',
    borderRadius: '4px',
    transition: 'background 0.2s, color 0.2s',
  },
  menuItemHoverStyle: {
    background: 'rgba(16, 185, 129, 0.15)',
    color: '#10B981',
  },
};
